/* Customer unloading acknowledgement. Does not select or change shipping rates. */
(() => {
  if (window.FibroluxUnloading) return;
  const attribute = 'Entladung mit Gerät bestätigt';
  const signatureAttribute = 'Entladebestätigung Warenkorb';
  const confirmation = 'Ja – geeignetes Entladegerät (z. B. Stapler, Radlader mit Palettengabel oder Kran) und Bedienperson stehen am Lieferort bereit; Entladung der Gitterroste durch den Kunden.';
  const properties = item => item.properties || {};
  const service = item => properties(item)._cut_kind === 'service' || properties(item)._restposten_plan_role === 'service' || /^S101[02](?:-|$)/i.test(item.sku || '');
  const group = item => properties(item)._cut_order ? `cut:${properties(item)._cut_order}` : properties(item)._restposten_plan_id ? `rest:${properties(item)._restposten_plan_id}` : '';
  const materialKey = items => {
    const counts = new Map();
    items.filter(item => !service(item)).forEach(item => counts.set(String(item.id), (counts.get(String(item.id)) || 0) + Number(item.quantity)));
    return Array.from(counts).sort((a,b) => a[0].localeCompare(b[0])).map(([id,q]) => `${id}:${q}`).join(',');
  };
  function number(value) {
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    return Number(String(value ?? '').trim().replace(/(?<=\d)\.(?=\d{3}(?:\D|$))/g, '').replace(',', '.')) || 0;
  }
  function millimetres(value) {
    const match = String(value || '').match(/([\d.,]+)\s*(mm|cm|m)?/i);
    return match ? number(match[1]) * (match[2]?.toLowerCase() === 'm' ? 1000 : match[2]?.toLowerCase() === 'cm' ? 10 : 1) : 0;
  }
  function isGrating(item, meta = {}) {
    const p = properties(item);
    // A fastening set consists of separate screws, nuts, plates and clamps.
    // Its configurator is named "gitterrost", but it does not contain a grating.
    if (p.set_key) return false;
    const handle = String(meta.handle || item.handle || '').toLowerCase();
    const identity = `${meta.product_type || item.product_type || ''} ${handle} ${item.product_title || item.title || ''}`;
    if (/zubehör|zubehoer|befestig|schraub|mutter|teller|klammer|klemm|halter|verbinder|endkapp|stopfen|stellfu|stellfü|stellfue|pfostenschuh|clip|fasten|screw|washer|bracket/i.test(identity)) return false;
    if (/^zuschnitt(?:-gitterrost)?$/.test(handle) || /^zuschnitt(?:\s+gitterrost)?$/i.test(item.product_title || item.title || '')) return false;
    if (p._unloading_kind) return p._unloading_kind === 'grating';
    if (p._cut_mode) return p._cut_mode === 'gitterrost';
    if (p._restposten_plan_id) return true;
    // Collections and template names are not product identities: accessories share
    // grating collections, and product.gitterrost is the fastening configurator.
    // The canonical handle also keeps recognition stable in translated storefronts.
    return /gitterrost|\broste?\b|grating|sicherheitsb(?:oden|öden|oeden|odoen)|\bplatte[\s-]+(?:P|ISO)[\s-]*\d/i.test(identity);
  }
  function plain(item, meta = {}) {
    let length = number(meta.length), width = number(meta.width);
    const options = item.options_with_values || [];
    if (!(length > 0 && width > 0)) {
      const dimensionOption = options.find(o => /abmess|dimension|größe|groesse|size/i.test(o.name || ''));
      const source = `${dimensionOption?.value || ''} ${item.variant_title || ''} ${item.product_title || item.title || ''}`;
      const pairs = Array.from(source.matchAll(/(\d[\d.,]*)\s*[x×]\s*(\d[\d.,]*)/gi)).map(m => [number(m[1]),number(m[2])]);
      const outline = pairs.sort((a,b) => b[0]*b[1]-a[0]*a[1])[0];
      const lengthOption = options.find(o => /^(länge|laenge|length|longueur)$/i.test(o.name || ''));
      if (lengthOption && !isGrating(item, meta)) {
        length = millimetres(lengthOption.value);
        width = outline ? Math.max(...outline) : 0;
      } else if (outline) [length, width] = outline;
    }
    return {length:Math.max(length,width),width:Math.min(length,width),weight:number(item.grams)*number(item.quantity),grating:isGrating(item,meta),title:item.product_title || item.title || 'Gitterrost'};
  }
  function evaluate(cart, metadata = []) {
    const items = (cart.items || []).filter(item => !service(item) && item.requires_shipping !== false);
    const meta = new Map(metadata.map(m => [String(m.id),m]));
    const seen = new Set(), parts = [];
    for (const item of items) {
      const id = group(item), p = properties(item);
      if (!id) {parts.push(plain(item,meta.get(String(item.id))));continue;}
      if (seen.has(id)) continue;
      seen.add(id);
      const members = items.filter(other => group(other) === id);
      const marker = members.find(other => properties(other)._unloading_delivery);
      if (marker) {
        const m = properties(marker);
        let dimensions = [];
        try {dimensions = JSON.parse(m._unloading_delivery);} catch (_) { /* An incomplete plan must be checked again. */ }
        const valid = m._unloading_materials === materialKey(members) && dimensions.length === 3;
        parts.push({length:valid ? number(dimensions[0]) : 0,width:valid ? number(dimensions[1]) : 0,weight:valid ? number(dimensions[2]) : 0,
          grating:members.some(other => isGrating(other,meta.get(String(other.id)))),title:item.product_title || item.title || 'Gitterrost'});
      } else if (members.every(other => properties(other)._cut_total_cuts === '0')) {
        members.forEach(other => parts.push(plain(other,meta.get(String(other.id)))));
      } else {
        // Older cart plans may only have a complete, quantity-bound parcel summary.
        const legacy = (cart.items || []).find(other => group(other) === id && properties(other)._shipping_plan_materials === materialKey(members));
        const m = properties(legacy || {});
        parts.push({length:number(m._shipping_finished_length_mm),width:number(m._shipping_finished_width_mm),weight:number(m._shipping_finished_weight_g),
          grating:members.some(other => isGrating(other,meta.get(String(other.id)))),title:item.product_title || item.title || 'Gitterrost'});
      }
    }
    const unknown = parts.filter(p => p.grating && !(p.length > 0 && p.width > 0));
    const long = parts.filter(p => p.grating && p.length > 2400);
    // Gratings are never bundled goods, regardless of their width or weight.
    // This acknowledgement applies only to gratings; profile bundles remain unaffected.
    return {required:long.length > 0,unknown:unknown.length > 0,parts:long};
  }
  function fingerprint(cart) {
    // A change marker, not a security token; normal Shopify checkout is controlled in the theme.
    const rows = (cart.items || []).filter(item => !service(item)).map(item => [String(item.id),Number(item.quantity),
      Object.entries(properties(item)).sort(([a],[b]) => a.localeCompare(b))]).sort((a,b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
    const value = JSON.stringify(rows); let hash = 2166136261;
    for (let i = 0; i < value.length; i++) hash = Math.imul(hash ^ value.charCodeAt(i), 16777619);
    return `unloading-v1-${(hash >>> 0).toString(16)}-${value.length}`;
  }
  const api = window.FibroluxUnloading = {evaluate,plain,fingerprint,materialKey};
  if (typeof document === 'undefined' || !document.querySelector) return;
  let node, cart, products = [], result, signature = '', accepted = false, saved = '', pending = 0, revision = 0, error = '', queue = Promise.resolve(), checking = false, forwarding = false;
  const root = window.Shopify?.routes?.root || '/';
  const getBox = () => document.querySelector('[data-unloading]');
  const text = {
    title:'Entladung der Gitterroste organisieren',
    info:'Ihre Lieferung enthält Gitterroste mit einem Liefermaß über 2.400 mm. Bei der Anlieferung müssen Sie die Entladung mit einem geeigneten Gerät sicherstellen, z. B. Gabelstapler, Radlader mit Palettengabel oder Kran. Eine Entladung von Hand ist nicht ausreichend.',
    label:'Ich bestätige, dass am Lieferort ein geeignetes Entladegerät und eine Person zur Bedienung bereitstehen und ich die Entladung der Gitterroste sicherstelle.',
    missing:'Bitte bestätigen Sie, dass Sie die Entladung mit einem geeigneten Gerät sicherstellen.',
    unknown:'Die Liefermaße dieses Gitterrost-Zuschnitts können nicht eindeutig geprüft werden. Bitte konfigurieren Sie den Zuschnitt erneut oder kontaktieren Sie uns vor der Bestellung.',
    failed:'Die Entladebestätigung konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.',
    saving:'Die Bestätigung wird gespeichert. Bitte warten Sie kurz.'
  };
  function canCheckout() {return !!result && !result.unknown && !error && pending === 0 && (!result.required || accepted && saved === signature);}
  api.canCheckout = canCheckout;
  function render() {
    const box = getBox();
    if (!box || !result) return;
    box.hidden = !result.required && !result.unknown && !error;
    box.querySelector('[data-unloading-title]').textContent = text.title;
    box.querySelector('[data-unloading-info]').textContent = result.unknown ? text.unknown : result.required ? text.info : text.failed;
    box.querySelector('[data-unloading-items]').textContent = result.required ? result.parts.map(p => `${p.title}: ${p.length.toLocaleString('de-DE')} × ${p.width.toLocaleString('de-DE')} mm`).join(' · ') : '';
    const checkbox = box.querySelector('[data-unloading-checkbox]');
    checkbox.checked = accepted;
    checkbox.required = result.required && !result.unknown;
    checkbox.disabled = result.unknown || !result.required;
    box.querySelector('[data-unloading-label]').hidden = result.unknown || !result.required;
    box.querySelector('[data-unloading-text]').textContent = text.label;
    box.querySelector('[data-unloading-error]').textContent = error;
    box.querySelector('[data-unloading-error]').hidden = !error;
    checkbox.setAttribute('aria-invalid',String(!!error));
    box.querySelector('[data-unloading-attribute]').value = accepted && saved === signature && result.required ? confirmation : '';
    box.querySelector('[data-unloading-signature]').value = accepted && saved === signature && result.required ? signature : '';
    document.documentElement.toggleAttribute('data-unloading-blocked',!canCheckout());
    // Required acknowledgements always use the normal cart form. Payment methods remain available in checkout.
    document.documentElement.toggleAttribute('data-unloading-required',result.required || result.unknown);
  }
  function save() {
    const version = ++revision, targetSignature = signature, yes = accepted && result.required && !result.unknown;
    const attrs = {[attribute]:yes ? confirmation : '',[signatureAttribute]:yes ? targetSignature : ''};
    pending++;
    render();
    queue = queue.then(async () => {
      try {
        const response = await fetch(`${root}cart/update.js`,{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({attributes:attrs})});
        if (!response.ok) throw new Error('unloading_save_failed');
        const updated = await response.json();
        if (version !== revision) return;
        if (fingerprint(updated) !== targetSignature) {applyCart(updated,false);return;}
        if (yes && (updated.attributes?.[signatureAttribute] !== targetSignature || updated.attributes?.[attribute] !== confirmation)) throw new Error('unloading_save_unconfirmed');
        cart = updated;
        saved = yes ? targetSignature : '';
        error = '';
      } catch (_) {
        if (version === revision) {saved='';accepted=false;error=text.failed;}
      } finally {pending--;render();}
    });
  }
  function applyCart(next, initial) {
    const nextSignature = fingerprint(next), changed = nextSignature !== signature;
    cart = next;
    result = evaluate(cart,products);
    if (changed) {signature=nextSignature;accepted=false;saved='';error='';}
    if (initial && result.required && cart.attributes?.[signatureAttribute] === signature && cart.attributes?.[attribute] === confirmation) {accepted=true;saved=signature;}
    render();
    if (!accepted && (cart.attributes?.[attribute] || cart.attributes?.[signatureAttribute])) save();
  }
  function readSnapshot() {
    const current = document.querySelector('[data-unloading-cart]');
    if (!current || current === node) return;
    const initial = !node;
    node = current;
    try {
      const data = JSON.parse(node.textContent);
      products = data.products || [];
      applyCart(data.cart,initial);
    } catch (_) {
      result = {required:false,unknown:true,parts:[]};error=text.unknown;render();
    }
  }
  document.addEventListener('change',event => {
    if (!event.target.matches('[data-unloading-checkbox]')) return;
    readSnapshot();
    accepted = event.target.checked;
    error = '';
    save();
  });
  function intercept(event) {
    const trigger = event.type === 'submit'
      ? event.target.matches('form[action$="/cart"]') || event.submitter?.name === 'checkout'
      : event.target.closest('button[name="checkout"], input[name="checkout"], a[href*="/checkout"], .additional-checkout-buttons');
    if (!trigger || !getBox()) return;
    if (forwarding) return;
    readSnapshot();
    if (error && !result?.unknown && !pending && (!result?.required || accepted && saved === signature)) {
      error='';
      if (!result.required) save();else render();
    }
    if (event.type === 'click' && trigger.matches('.additional-checkout-buttons') && canCheckout() && !result.required) return;
    event.preventDefault();event.stopImmediatePropagation();
    if (canCheckout()) {
      if (checking) return;
      checking = true;
      const expected = signature, target = event.type === 'submit' ? event.target : trigger, submitter = event.submitter;
      fetch(`${root}cart.js`,{headers:{Accept:'application/json'},cache:'no-store'})
        .then(response => {if (!response.ok) throw new Error('unloading_check_failed');return response.json();})
        .then(current => {
          if (fingerprint(current) !== expected) {
            // Reload the actual server-rendered dimensions and require a fresh acknowledgement.
            window.location.reload();return;
          }
          if (result.required && (current.attributes?.[attribute] !== confirmation || current.attributes?.[signatureAttribute] !== expected)) {
            saved='';accepted=false;error=text.missing;render();return;
          }
          forwarding = true;
          try {
            if (event.type === 'submit') target.requestSubmit(submitter || undefined);
            else target.click();
          } finally {forwarding = false;}
        })
        .catch(() => {error=text.failed;render();})
        .finally(() => {checking=false;});
      return;
    }
    error = result?.unknown ? text.unknown : pending ? text.saving : error || text.missing;
    render();
    const box = getBox();box.scrollIntoView({block:'center',behavior:'smooth'});
    box.querySelector('[data-unloading-checkbox]').focus({preventScroll:true});
  }
  document.addEventListener('click',intercept,true);
  document.addEventListener('submit',intercept,true);
  const start = () => {
    readSnapshot();
    const footer = document.getElementById('main-cart-footer');
    if (footer) new MutationObserver(readSnapshot).observe(footer,{childList:true,subtree:true});
    if (typeof subscribe === 'function') subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
      readSnapshot();
      if (event?.cartData) applyCart(event.cartData,false);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',start);else start();
})();
